import { useCallback, useEffect, useMemo, useState } from 'react';
import SectionTabs from '../../components/SectionTabs';
import { SERIES_TABS } from '../../lib/section-tabs';
import { api } from '../../lib/api';
import { clearSwrCache } from '../../lib/swrCache';
import SeriesEditModal from '../../components/SeriesEditModal';
import type { EventSeries, SeriesStatus, SeriesType } from '../../types';
import { LoadingBlock } from '../../components/Spinner';
import { Pagination, usePagination } from '../../components/Pagination';
import BulkJobModal from '../../components/BulkJobModal';
import { submitJob, type JobTarget } from '../../lib/jobs';
import { confirmDialog, toast } from '../../lib/dialogs';

type StatusFilter = 'all' | SeriesStatus;
type TypeFilter = 'all' | SeriesType;

interface ScanResult { ingested: number; issues: string[]; validationConfidence?: number; validationReason?: string }
interface CleanupResult { dryRun: boolean; matched: number; series: Array<{ rowKey: string; name: string; reason: string }> }
interface DiscoverResult {
  dryRun: boolean;
  wave: number;
  monthsBack: number;
  minOccurrences: number;
  llmUsed: boolean;
  eventsScanned: number;
  handlesConsidered: number;
  venueHandlesBlocked: number;
  knownHandlesBlocked: number;
  candidatesCreated: number;
  candidatesRejected: number;
  venuesCreated: number;
  venuesMatched: number;
  eventsStampedNew: number;
  eventsStampedExisting: number;
  apifyCostPerScan: number;
  nextWaveScanCount: number;
  nextWaveCostUsd: number;
  created: Array<{ rowKey: string; name: string; handle: string; occurrences: number; kind: string; confidence: number; reason: string }>;
  rejected: Array<{ handle: string; occurrences: number; kind: string; reason: string }>;
  venues: Array<{ rowKey: string; name: string; handle: string; occurrences: number; confidence: number; reason: string; viaPlaces: boolean; created: boolean }>;
  matchedExisting: Array<{ eventId: string; seriesId: string; name: string }>;
}

const STATUS_LABELS: Record<SeriesStatus, string> = {
  candidate: 'Candidate',
  active: 'Active',
  dismissed: 'Dismissed',
};

function confidenceBadge(score: number | undefined): { className: string; label: string } {
  if (score == null) return { className: 'bg-light text-dark border', label: '—' };
  if (score >= 70) return { className: 'bg-success', label: String(score) };
  if (score >= 40) return { className: 'bg-warning text-dark', label: String(score) };
  return { className: 'bg-danger', label: String(score) };
}

function relativeAgo(iso?: string): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

function emptySeries(): Partial<EventSeries> {
  return {
    partitionKey: 'recurring-event',
    name: '',
    status: 'active',
    instagramHandle: '',
    facebookUrl: '',
    website: '',
    description: '',
    active: true,
  };
}

export default function AdminSeriesPage() {
  const [series, setSeries] = useState<EventSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [search, setSearch] = useState('');

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Edit modal
  const [editing, setEditing] = useState<EventSeries | Partial<EventSeries> | null>(null);

  // Cleanup
  const [cleanupReport, setCleanupReport] = useState<CleanupResult | null>(null);
  const [discoverReport, setDiscoverReport] = useState<DiscoverResult | null>(null);
  const [discoverWave, setDiscoverWave] = useState<number>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem('mhe.discoverWave') : null;
    const n = stored ? Number(stored) : 1;
    return Number.isFinite(n) && n >= 1 ? n : 1;
  });
  useEffect(() => {
    try { window.localStorage.setItem('mhe.discoverWave', String(discoverWave)); } catch { /* ignore */ }
  }, [discoverWave]);
  /**
   * Currently-tracked server-side bulk job. The work runs on the API queue
   * worker; closing the modal does NOT abort it. See `lib/jobs.ts`.
   */
  const [activeJob, setActiveJob] = useState<{ jobPk: string; jobId: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<EventSeries[]>('/api/mgmt/series');
      setSeries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load series');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return series.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (typeFilter !== 'all' && s.partitionKey !== typeFilter) return false;
      if (q) {
        const hay = [s.name, s.instagramHandle, s.facebookUrl, s.website, s.description, s.rowKey]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [series, statusFilter, typeFilter, search]);

  const counts = useMemo(() => ({
    all: series.length,
    candidate: series.filter((s) => s.status === 'candidate').length,
    active: series.filter((s) => s.status === 'active').length,
    dismissed: series.filter((s) => s.status === 'dismissed').length,
  }), [series]);

  const allSelected = visible.length > 0 && visible.every((s) => selected.has(s.rowKey));
  function toggleSelectAll(): void {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map((s) => s.rowKey)));
    }
  }

  // Client-side pagination across the filtered (`visible`) list.
  const pagination = usePagination(visible, {
    defaultPageSize: 50,
    storageKey: 'mh-admin-series-page-size',
    resetKey: { statusFilter, typeFilter, search },
  });
  const paged = pagination.slice(visible);
  function toggleSelect(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function refresh(): Promise<void> { await load(); setSelected(new Set()); }

  async function bulk(action: 'promote' | 'dismiss' | 'delete'): Promise<void> {
    if (selected.size === 0) return;
    const verb = action === 'promote' ? 'promote' : action === 'dismiss' ? 'dismiss' : 'DELETE';
    if (action === 'delete' && !(await confirmDialog({
      title: 'Delete series',
      body: `Permanently delete ${selected.size} series?`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    if (action !== 'delete' && !(await confirmDialog({
      title: `${verb} series`,
      body: `${verb} ${selected.size} series?`,
      confirmLabel: verb,
    }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/mgmt/series/bulk', { ids: Array.from(selected), action });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk action failed');
    } finally {
      setBusy(false);
    }
  }

  async function deleteOne(s: EventSeries): Promise<void> {
    if (!(await confirmDialog({
      title: 'Delete series',
      body: `Permanently delete "${s.name}"?`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    setBusy(true);
    try {
      await api.del(`/api/mgmt/series/${encodeURIComponent(s.rowKey)}`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(s: EventSeries, status: SeriesStatus): Promise<void> {
    if (s.status === status) return;
    setBusy(true);
    try {
      await api.patch(`/api/mgmt/series/${encodeURIComponent(s.rowKey)}`, { status });
      clearSwrCache('series:public');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Status change failed');
    } finally {
      setBusy(false);
    }
  }

  async function scan(s: EventSeries, platform: 'instagram' | 'facebook'): Promise<void> {
    setScanning(s.rowKey + ':' + platform);
    setError(null);
    try {
      const path = platform === 'instagram' ? 'scan' : 'scan-facebook';
      const r = await api.post<ScanResult>(`/api/mgmt/series/${encodeURIComponent(s.rowKey)}/${path}`);
      const issues = r.issues.length ? `\nIssues: ${r.issues.join('; ')}` : '';
      const conf = r.validationConfidence != null
        ? `\nConfidence: ${r.validationConfidence}${r.validationReason ? ` (${r.validationReason})` : ''}`
        : '';
      toast.success(`Scanned ${s.name} on ${platform}: ${r.ingested} ingested.${conf}${issues}`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scan failed');
    } finally {
      setScanning(null);
    }
  }

  function openCreate(): void {
    setEditing(emptySeries());
  }
  function openEdit(s: EventSeries): void {
    setEditing(s);
  }
  function closeEdit(): void {
    setEditing(null);
  }

  function onSeriesSaved(): void {
    // Invalidate the public series cache so VenuesPublicPage / SeriesPublicPage
    // and any detail pages serve fresh data on next view.
    clearSwrCache('series:public');
    void refresh();
  }

  async function setSeriesType(s: EventSeries, partitionKey: SeriesType): Promise<void> {
    if (s.partitionKey === partitionKey) return;
    setBusy(true);
    try {
      await api.patch(`/api/mgmt/series/${encodeURIComponent(s.rowKey)}`, { partitionKey });
      clearSwrCache('series:public');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Type change failed');
    } finally {
      setBusy(false);
    }
  }

  async function runCleanup(dryRun: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<CleanupResult>(`/api/mgmt/series/cleanup${dryRun ? '?dryRun=1' : ''}`);
      setCleanupReport(r);
      if (!dryRun) await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cleanup failed');
    } finally {
      setBusy(false);
    }
  }

  async function runRescoreValidation(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ updated: number; total: number; scanned: number }>('/api/mgmt/series/rescore-validation');
      toast.success(`Rescored ${r.scanned} scanned series; ${r.updated} updated (of ${r.total} total).`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rescore failed');
    } finally {
      setBusy(false);
    }
  }

  async function runReclassifyPending(dryRun: boolean, all: boolean = false): Promise<void> {
    const verb = dryRun ? 'Preview' : (all ? 'Reclassify ALL' : 'Reclassify');
    if (!dryRun && !(await confirmDialog({
      title: `${verb} candidates`,
      body:
        `${verb} ${all ? 'every' : 'LLM-failed'} candidate via the classifier?\n\n` +
        `Noise/venue verdicts will be auto-dismissed.\n` +
        `Estimated cost ~$0.005 per 100 candidates.`,
      confirmLabel: verb,
    }))) return;
    setBusy(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (dryRun) qs.set('dryRun', '1');
      if (all) qs.set('all', '1');
      qs.set('limit', '500');
      const r = await api.post<{
        dryRun: boolean;
        considered: number;
        targets: number;
        classified: number;
        failedBatches: number;
        dismissed: number;
        kept: number;
        noChange: number;
      }>(`/api/mgmt/series/reclassify-pending?${qs.toString()}`);
      const summary = `${r.dryRun ? '[DRY RUN] ' : ''}` +
        `Targets: ${r.targets} (of ${r.considered} candidates)\n` +
        `Classified: ${r.classified}\n` +
        `Dismissed (noise/venue): ${r.dismissed}\n` +
        `Kept/updated: ${r.kept} (${r.noChange} unchanged)\n` +
        `Failed batches: ${r.failedBatches}`;
      toast.info(summary);
      if (!dryRun) await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reclassify failed');
    } finally {
      setBusy(false);
    }
  }

  async function runDiscover(dryRun: boolean, reclassifyDismissed: boolean = false): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (dryRun) qs.set('dryRun', '1');
      qs.set('monthsBack', '12');
      qs.set('minOccurrences', '1');
      qs.set('wave', String(discoverWave));
      if (reclassifyDismissed) qs.set('reclassifyDismissed', '1');
      const r = await api.post<DiscoverResult>(`/api/mgmt/series/discover?${qs.toString()}`);
      setDiscoverReport(r);
      if (!dryRun) {
        setDiscoverWave((w) => w + 1);
        await refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Discovery failed');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Submit a server-side `series-scan-ig` job. The fan-out is queue-driven
   * on the API; closing the tab no longer aborts. Replaces the previous
   * client-side worker pool — Apify cost is identical, but the job survives.
   */
  async function submitSeriesScanJob(label: string, targets: EventSeries[]): Promise<void> {
    if (!targets.length) return;
    try {
      const res = await submitJob({
        kind: 'series-scan-ig',
        label,
        targets: targets.map<JobTarget>((s) => ({
          kind: 'series-scan-ig',
          seriesId: s.rowKey,
          label: s.name
        }))
      });
      setActiveJob({ jobPk: res.jobPk, jobId: res.jobId });
    } catch (e) {
      toast.error(`Could not submit job: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function bulkScanInstagram(): Promise<void> {
    const targets = series.filter((s) => selected.has(s.rowKey) && s.instagramHandle);
    await submitSeriesScanJob(`Scan IG for ${targets.length} series`, targets);
  }

  type CandidateScanMode = 'unscanned' | 'stale' | 'all';

  function candidateTargets(mode: CandidateScanMode): EventSeries[] {
    const now = Date.now();
    return series.filter((s) => {
      if (s.status !== 'candidate' || !s.instagramHandle) return false;
      if (mode === 'all') return true;
      if (mode === 'unscanned') return !s.lastSocialScanAt;
      // stale: never-scanned OR cooldown elapsed
      if (!s.lastSocialScanAt) return true;
      if (!s.nextScanEligibleAt) return true;
      return new Date(s.nextScanEligibleAt).getTime() <= now;
    });
  }

  const candidateCounts = useMemo(() => ({
    unscanned: candidateTargets('unscanned').length,
    stale: candidateTargets('stale').length,
    all: candidateTargets('all').length,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [series]);

  async function scanCandidates(mode: CandidateScanMode): Promise<void> {
    const targets = candidateTargets(mode);
    if (targets.length === 0) {
      const msg = mode === 'unscanned'
        ? 'No unscanned candidates with Instagram handles.'
        : mode === 'stale'
          ? 'No candidates past their cooldown — try "force all" if you want to rescan.'
          : 'No candidates with Instagram handles to scan.';
      toast.info(msg);
      return;
    }
    const cost = (targets.length * 0.005).toFixed(3);
    const label = mode === 'unscanned' ? 'unscanned candidates'
      : mode === 'stale' ? 'stale + unscanned candidates'
      : 'ALL candidates (force rescan)';
    if (!(await confirmDialog({
      title: 'Scan candidates',
      body: `Scan IG for ${targets.length} ${label}? Estimated Apify cost ~$${cost}.`,
      confirmLabel: 'Scan',
    }))) return;
    await submitSeriesScanJob(`Scan IG for ${targets.length} candidates`, targets);
  }

  return (
    <SectionTabs ariaLabel="Series sections" items={SERIES_TABS} fluid>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h1 className="h3 mb-0 me-auto">Manage series</h1>
        <button type="button" className="btn btn-primary btn-sm" onClick={openCreate} disabled={busy}>
          + New series
        </button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void refresh()} disabled={loading || busy}>
          Refresh
        </button>
      </div>

      <p className="text-muted small mb-3">
        Recurring brands (Puzzled Pint, Stitch &amp; Bitch), aggregators (Riverside Arts Market), and organizers that
        host events at multiple venues. Candidates are auto-discovered from extracted events; promote them to{' '}
        <strong>active</strong> to enable scheduled scanning of their own IG / FB / website.
      </p>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button type="button" className="btn-close float-end" aria-label="Dismiss" onClick={() => setError(null)} />
        </div>
      )}

      {/* Status tabs */}
      <ul className="nav nav-pills mb-3" role="tablist">
        {(['all', 'candidate', 'active', 'dismissed'] as StatusFilter[]).map((s) => (
          <li key={s} className="nav-item">
            <button
              type="button"
              className={`nav-link ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s)}
              aria-pressed={statusFilter === s}
            >
              {s === 'all' ? 'All' : STATUS_LABELS[s]}
              <span className="badge bg-light text-dark ms-2">{counts[s]}</span>
            </button>
          </li>
        ))}
      </ul>

      {/* Filters row */}
      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <label htmlFor="series-search" className="form-label visually-hidden">Search</label>
          <input
            id="series-search"
            type="search"
            className="form-control form-control-sm"
            placeholder="Search name, handle, URL…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label htmlFor="series-type-filter" className="form-label visually-hidden">Type</label>
          <select
            id="series-type-filter"
            className="form-select form-select-sm"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          >
            <option value="all">All types</option>
            <option value="recurring-event">Recurring event</option>
            <option value="aggregator">Aggregator</option>
            <option value="organizer">Organizer</option>
          </select>
        </div>
        <div className="col-md-5 d-flex flex-wrap gap-2 justify-content-md-end">
          <button type="button" className="btn btn-outline-primary btn-sm"
            onClick={() => void runDiscover(true)} disabled={busy}
            title="Preview wave: classify @mentions in extracted events without creating candidates.">
            Discover preview (wave {discoverWave})
          </button>
          <button type="button" className="btn btn-primary btn-sm"
            onClick={() => void runDiscover(false)} disabled={busy}
            title="Apply wave: create candidates and stamp seriesId on matched events. Each click runs the next wave — scan candidates afterward to feed the next wave.">
            Discover wave {discoverWave}
          </button>
          <button type="button" className="btn btn-link btn-sm text-muted px-1"
            onClick={() => {
              void (async () => {
                if (await confirmDialog({
                  title: 'Reset wave counter',
                  body: `Reset wave counter to 1? (current: ${discoverWave})`,
                  confirmLabel: 'Reset',
                })) setDiscoverWave(1);
              })();
            }}
            disabled={busy}
            title="Reset the wave counter (cosmetic — the backend wave param is informational only).">
            Reset wave
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm"
            onClick={() => void runDiscover(false, true)} disabled={busy}
            title="Re-run the classifier against previously-dismissed handles and promote any that the (now wider) prompt accepts back to candidate.">
            Reconsider dismissed
          </button>
          <button type="button" className="btn btn-outline-info btn-sm"
            onClick={() => void runReclassifyPending(false, false)} disabled={busy}
            title="Re-run the LLM classifier against candidates whose original classification failed (e.g. '(LLM failed — kept for manual review)'). Noise/personal accounts get auto-dismissed.">
            Reclassify LLM-failed
          </button>
          <button type="button" className="btn btn-outline-info btn-sm"
            onClick={() => void runReclassifyPending(false, true)} disabled={busy}
            title="Re-run the LLM classifier against EVERY candidate (not just LLM-failed ones). More expensive — use sparingly.">
            Reclassify all candidates
          </button>
          <div className="btn-group btn-group-sm" role="group" aria-label="Scan candidates">
            <button type="button" className="btn btn-outline-warning"
              onClick={() => void scanCandidates('unscanned')} disabled={busy || !!activeJob || candidateCounts.unscanned === 0}
              title="Scan only candidates that have NEVER been scanned. Cheapest path to feed the next wave.">
              Scan unscanned ({candidateCounts.unscanned})
            </button>
            <button type="button" className="btn btn-outline-warning"
              onClick={() => void scanCandidates('stale')} disabled={busy || !!activeJob || candidateCounts.stale === 0}
              title="Scan unscanned candidates plus any whose cooldown has elapsed.">
              Scan stale ({candidateCounts.stale})
            </button>
            <button type="button" className="btn btn-outline-danger"
              onClick={() => void scanCandidates('all')} disabled={busy || !!activeJob || candidateCounts.all === 0}
              title="Force rescan EVERY candidate, ignoring cooldowns. Most expensive.">
              Force all ({candidateCounts.all})
            </button>
          </div>
          <button type="button" className="btn btn-outline-secondary btn-sm"
            onClick={() => void runCleanup(true)} disabled={busy}>
            Cleanup preview
          </button>
          <button type="button" className="btn btn-outline-danger btn-sm"
            onClick={() => void runCleanup(false)} disabled={busy}>
            Cleanup old candidates
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm"
            onClick={() => void runRescoreValidation()} disabled={busy}
            title="Recompute post-scan validation scores from existing scan signals (no Apify cost).">
            Rescore confidence
          </button>
        </div>
      </div>

      {cleanupReport && (
        <div className="alert alert-info">
          <div className="d-flex align-items-start">
            <div className="flex-grow-1">
              <strong>{cleanupReport.dryRun ? 'Cleanup preview' : 'Cleanup completed'}:</strong>{' '}
              {cleanupReport.matched} candidate{cleanupReport.matched === 1 ? '' : 's'} older than 30 days
              {cleanupReport.dryRun ? ' would be deleted.' : ' deleted.'}
              {cleanupReport.series.length > 0 && (
                <ul className="small mb-0 mt-1">
                  {cleanupReport.series.slice(0, 10).map((x) => (
                    <li key={x.rowKey}>{x.name} <span className="text-muted">({x.reason})</span></li>
                  ))}
                  {cleanupReport.series.length > 10 && (
                    <li className="text-muted">… and {cleanupReport.series.length - 10} more</li>
                  )}
                </ul>
              )}
            </div>
            <button type="button" className="btn-close" aria-label="Dismiss" onClick={() => setCleanupReport(null)} />
          </div>
        </div>
      )}

      {discoverReport && (
        <div className="alert alert-primary">
          <div className="d-flex align-items-start">
            <div className="flex-grow-1">
              <strong>{discoverReport.dryRun ? `Wave ${discoverReport.wave} preview` : `Wave ${discoverReport.wave} applied`}:</strong>{' '}
              scanned {discoverReport.eventsScanned} events from the past {discoverReport.monthsBack} months
              ({discoverReport.venueHandlesBlocked} venue handles + {discoverReport.knownHandlesBlocked} known handles filtered out, {discoverReport.handlesConsidered} handles classified
              {discoverReport.llmUsed ? ' via LLM' : ' (LLM skipped)'}).
              <ul className="small mb-2 mt-1">
                <li><strong>{discoverReport.candidatesCreated}</strong> kept as candidate{discoverReport.candidatesCreated === 1 ? '' : 's'}</li>
                <li><strong>{discoverReport.candidatesRejected}</strong> rejected as noise &mdash; persisted as dismissed so future waves won&rsquo;t re-classify</li>
                {(discoverReport.venuesCreated > 0 || discoverReport.venuesMatched > 0) && (
                  <li>
                    <strong>{discoverReport.venuesCreated}</strong> venue{discoverReport.venuesCreated === 1 ? '' : 's'} auto-created
                    {discoverReport.venuesMatched > 0 && <> · <strong>{discoverReport.venuesMatched}</strong> matched existing</>}
                    {' '}&mdash; enrichment will fill in website + Facebook automatically
                  </li>
                )}
                <li><strong>{discoverReport.eventsStampedExisting}</strong> events linked to existing series</li>
                <li><strong>{discoverReport.eventsStampedNew}</strong> events linked to new candidates</li>
              </ul>
              <div className="alert alert-info py-2 px-3 mb-2 small">
                <strong>Next wave estimate:</strong> {discoverReport.nextWaveScanCount} IG scan{discoverReport.nextWaveScanCount === 1 ? '' : 's'}
                @ ${discoverReport.apifyCostPerScan.toFixed(4)} each = <strong>~${discoverReport.nextWaveCostUsd.toFixed(4)}</strong> Apify spend.
                {' '}Click <em>Scan unscanned</em> to feed wave {discoverReport.wave + 1}.
              </div>
              {discoverReport.created.length > 0 && (
                <details className="small" open>
                  <summary>Created candidates ({discoverReport.created.length})</summary>
                  <ul className="mb-0 mt-1">
                    {discoverReport.created.slice(0, 50).map((c) => (
                      <li key={c.handle}>
                        <strong>{c.name}</strong> @{c.handle}{' '}
                        <span className="badge bg-light text-dark">{c.kind}</span>{' '}
                        <span className="text-muted">({c.occurrences}×, conf {c.confidence}) — {c.reason}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {discoverReport.venues && discoverReport.venues.length > 0 && (
                <details className="small mt-1" open>
                  <summary>Venues created/matched ({discoverReport.venues.length})</summary>
                  <p className="text-muted mb-1 mt-1">
                    Auto-created from @-mention discovery. Rows tagged <em>Places</em> were upgraded via Google Places (have lat/lng + address); rows tagged <em>handle-only</em> are unverified candidates living under partition <code>Unverified</code> until enrichment + admin review.
                  </p>
                  <ul className="mb-0 mt-1">
                    {discoverReport.venues.slice(0, 50).map((v) => (
                      <li key={v.handle}>
                        <a href={`https://instagram.com/${v.handle}`} target="_blank" rel="noopener noreferrer">@{v.handle}</a>{' '}
                        <strong>{v.name}</strong>{' '}
                        <span className="badge bg-light text-dark">{v.viaPlaces ? 'Places' : 'handle-only'}</span>{' '}
                        <span className="badge bg-light text-dark">{v.created ? 'created' : 'matched existing'}</span>{' '}
                        <span className="text-muted">({v.occurrences}×, conf {v.confidence}) — {v.reason}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {discoverReport.rejected.length > 0 && (
                <details className="small mt-1">
                  <summary>Rejected ({discoverReport.rejected.length})</summary>
                  <ul className="mb-0 mt-1">
                    {discoverReport.rejected.slice(0, 50).map((r) => (
                      <li key={r.handle}>
                        @{r.handle} <span className="badge bg-light text-dark">{r.kind}</span>{' '}
                        <span className="text-muted">({r.occurrences}×) — {r.reason}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <button type="button" className="btn-close" aria-label="Dismiss" onClick={() => setDiscoverReport(null)} />
          </div>
        </div>
      )}

      {/* Server-side bulk job progress modal. The job runs entirely on the
          API; closing the modal does NOT abort it. */}
      <BulkJobModal
        jobPk={activeJob?.jobPk}
        jobId={activeJob?.jobId}
        onClose={() => setActiveJob(null)}
        onComplete={() => { void refresh(); }}
      />

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="alert alert-secondary d-flex flex-wrap align-items-center gap-2">
          <span><strong>{selected.size}</strong> selected</span>
          <button type="button" className="btn btn-success btn-sm" onClick={() => void bulk('promote')} disabled={busy || !!activeJob}>
            Promote to active
          </button>
          <button type="button" className="btn btn-warning btn-sm" onClick={() => void bulk('dismiss')} disabled={busy || !!activeJob}>
            Dismiss
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void bulkScanInstagram()} disabled={busy || !!activeJob}
            title="Scan Instagram for the selected series. Skips ones without an IG handle.">
            Scan IG (selected)
          </button>
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => void bulk('delete')} disabled={busy || !!activeJob}>
            Delete
          </button>
          <button type="button" className="btn btn-link btn-sm ms-auto" onClick={() => setSelected(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      {loading ? (
        <LoadingBlock label="Loading series…" />
      ) : visible.length === 0 ? (
        <div className="alert alert-light border">
          No series match the current filters. {series.length === 0 && (
            <>Click <strong>+ New series</strong> to add your first one (e.g. Puzzled Pint Jacksonville).</>
          )}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th scope="col" style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all visible series"
                  />
                </th>
                <th scope="col">Name</th>
                <th scope="col">Type</th>
                <th scope="col">Status</th>
                <th scope="col" title="Post-scan validation score: handle resolves + posts ingested + events extracted + recency. — = never scanned.">Conf.</th>
                <th scope="col">Socials</th>
                <th scope="col">Last scan</th>
                <th scope="col">Captured</th>
                <th scope="col" className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((s) => {
                const isSelected = selected.has(s.rowKey);
                const lastScan = [s.lastSocialScanAt, s.lastFacebookScanAt, s.lastWebsiteScanAt]
                  .filter(Boolean)
                  .map((d) => new Date(d as string).getTime())
                  .reduce<number | null>((max, t) => max == null || t > max ? t : max, null);
                const lastScanIso = lastScan ? new Date(lastScan).toISOString() : undefined;
                return (
                  <tr key={s.rowKey}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isSelected}
                        onChange={() => toggleSelect(s.rowKey)}
                        aria-label={`Select ${s.name}`}
                      />
                    </td>
                    <td>
                      <div className="fw-semibold">{s.name}</div>
                      {s.description && <div className="small text-muted">{s.description}</div>}
                      {s.discoveryReason && (
                        <div className="small text-muted fst-italic">Discovered: {s.discoveryReason}</div>
                      )}
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={s.partitionKey}
                        disabled={busy}
                        onChange={(ev) => void setSeriesType(s, ev.target.value as SeriesType)}
                        aria-label={`Type for ${s.name}`}
                        title="Change series type"
                      >
                        <option value="recurring-event">Recurring event</option>
                        <option value="aggregator">Aggregator</option>
                        <option value="organizer">Organizer</option>
                      </select>
                    </td>
                    <td>
                      <select
                        className={`form-select form-select-sm mh-status-select mh-status-select--${s.status}`}
                        value={s.status}
                        disabled={busy}
                        onChange={(ev) => void setStatus(s, ev.target.value as SeriesStatus)}
                        aria-label={`Status for ${s.name}`}
                        title="Change series status"
                      >
                        <option value="active">Active</option>
                        <option value="candidate">Candidate</option>
                        <option value="dismissed">Dismissed</option>
                      </select>
                    </td>
                    <td>
                      {(() => {
                        const cb = confidenceBadge(s.validationConfidence);
                        const tooltip = s.validationConfidence != null
                          ? `${s.validationReason ?? ''}${s.lastValidatedAt ? ` • ${relativeAgo(s.lastValidatedAt)}` : ''}${s.discoveryConfidence != null ? ` • discovery ${s.discoveryConfidence}` : ''}`
                          : (s.discoveryConfidence != null ? `Not scanned yet • discovery ${s.discoveryConfidence}` : 'Not scanned yet');
                        return <span className={`badge ${cb.className}`} title={tooltip}>{cb.label}</span>;
                      })()}
                    </td>
                    <td className="small">
                      {s.instagramHandle && (
                        <div>
                          <span className="text-muted">IG:</span>{' '}
                          <a href={`https://instagram.com/${s.instagramHandle}`} target="_blank" rel="noopener noreferrer">
                            @{s.instagramHandle}
                          </a>
                        </div>
                      )}
                      {s.facebookUrl && (
                        <div>
                          <span className="text-muted">FB:</span>{' '}
                          <a href={s.facebookUrl} target="_blank" rel="noopener noreferrer">link</a>
                        </div>
                      )}
                      {s.website && (
                        <div>
                          <span className="text-muted">Web:</span>{' '}
                          <a href={s.website} target="_blank" rel="noopener noreferrer">site</a>
                        </div>
                      )}
                      {!s.instagramHandle && !s.facebookUrl && !s.website && (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="small text-muted">{relativeAgo(lastScanIso)}</td>
                    <td className="small text-muted">{relativeAgo(s.lastEventCapturedAt)}</td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm" role="group" aria-label={`Actions for ${s.name}`}>
                        {s.status === 'candidate' && (
                          <button type="button" className="btn btn-outline-success"
                            onClick={() => void setStatus(s, 'active')} disabled={busy}>
                            Promote
                          </button>
                        )}
                        {s.status === 'active' && (
                          <button type="button" className="btn btn-outline-warning"
                            onClick={() => void setStatus(s, 'dismissed')} disabled={busy}>
                            Dismiss
                          </button>
                        )}
                        {s.status === 'dismissed' && (
                          <button type="button" className="btn btn-outline-success"
                            onClick={() => void setStatus(s, 'active')} disabled={busy}>
                            Reactivate
                          </button>
                        )}
                        {s.instagramHandle && (
                          <button type="button" className="btn btn-outline-secondary"
                            onClick={() => void scan(s, 'instagram')}
                            disabled={busy || scanning === s.rowKey + ':instagram'}>
                            {scanning === s.rowKey + ':instagram' ? 'Scanning…' : 'Scan IG'}
                          </button>
                        )}
                        {s.facebookUrl && (
                          <button type="button" className="btn btn-outline-secondary"
                            onClick={() => void scan(s, 'facebook')}
                            disabled={busy || scanning === s.rowKey + ':facebook'}>
                            {scanning === s.rowKey + ':facebook' ? 'Scanning…' : 'Scan FB'}
                          </button>
                        )}
                        <button type="button" className="btn btn-outline-primary"
                          onClick={() => openEdit(s)} disabled={busy}>
                          Edit
                        </button>
                        <button type="button" className="btn btn-outline-danger"
                          onClick={() => void deleteOne(s)} disabled={busy}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          totalItems={visible.length}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
          pageSizeOptions={pagination.pageSizeOptions}
          ariaLabel="Series pagination"
          itemLabel="series"
        />
      )}

      {/* Edit / create modal */}
      <SeriesEditModal
        series={editing}
        onClose={closeEdit}
        onSaved={onSeriesSaved}
      />
    </SectionTabs>
  );
}
